import Image from 'next/image'
import Link from 'next/link'
import { Badge } from '@ui/base/badge'
import { Button } from '@ui/base/button'
import { Card, CardContent } from '@ui/base/card'
import { pluralize } from '@utils/formaters'
import { typeClass } from '@utils/typography'
import { SquarePenIcon, WarehouseIcon } from 'lucide-react'

import { marketplaceLabels, shipmentTypeLabels } from '../lib/labels'
import { type Fulfillment } from '../lib/to-fulfillment'
import { MarketplaceTag } from './marketplace-tag'
import { ReviewSource, type ReviewSourceCode } from './review-source'
import { Stars } from './stars'

const numberFormat = new Intl.NumberFormat('ru-RU')
const mutedCaption = `${typeClass.caption} text-muted-foreground`
const mutedSupporting = `${typeClass.supporting} text-muted-foreground`
const ratingCountForms = ['оценка', 'оценки', 'оценок']

/** Тот же блок, что в карточке фулфилмента, только мельче: источник сверху, под ним звёзды и числа. */
const RatingRow = ({
  source,
  rating,
}: {
  source: ReviewSourceCode
  rating: { value: number; reviewCount: number }
}) => (
  <div className="grid gap-0.5">
    <ReviewSource source={source} className={mutedCaption} />
    <span className={`flex flex-wrap items-center gap-x-1.5 gap-y-0.5 ${typeClass.supporting}`}>
      <Stars value={rating.value} />
      <span className={typeClass.label}>{rating.value.toLocaleString('ru-RU')}</span>
      <span className={mutedCaption}>
        {numberFormat.format(rating.reviewCount)} {pluralize(rating.reviewCount, ratingCountForms)}
      </span>
    </span>
  </div>
)

const Ratings = ({ item }: { item: Fulfillment }) => {
  const { evirma, yandex, twoGis } = item.ratings
  if (!evirma && !yandex && !twoGis) {
    return <span className={mutedSupporting}>Оценок пока нет</span>
  }

  return (
    <div className="flex flex-wrap gap-x-6 gap-y-2">
      {evirma && <RatingRow source="evirma" rating={evirma} />}
      {yandex && <RatingRow source="yandex" rating={yandex} />}
      {twoGis && <RatingRow source="two_gis" rating={twoGis} />}
    </div>
  )
}

const FulfillmentLogo = ({ item }: { item: Fulfillment }) =>
  item.coverFileId ? (
    <Image
      src={`/api/server/fulfillment/${item.slug}/cover`}
      alt=""
      width={112}
      height={112}
      unoptimized
      className="size-14 shrink-0 rounded-lg object-cover"
    />
  ) : (
    <Badge variant="info" className="size-14 shrink-0 rounded-lg" aria-hidden="true">
      <span className={typeClass.sectionTitle}>{item.name.slice(0, 1).toLocaleUpperCase('ru')}</span>
    </Badge>
  )

/** Короткая строка «о компании»: клиенты, площадь склада и год начала работы. */
const businessFacts = (item: Fulfillment) => {
  const facts: string[] = []
  if (item.activeClientCount !== null) {
    facts.push(`${numberFormat.format(item.activeClientCount)}+ активных клиентов`)
  }
  if (item.areaSquareMeters !== null) {
    facts.push(`${numberFormat.format(item.areaSquareMeters)} м²`)
  }
  if (item.operationStartedYear !== null) {
    facts.push(`с ${item.operationStartedYear} года`)
  }
  return facts.join(' · ')
}

const ShipmentTypeTags = ({ values }: { values: string[] }) =>
  values.map((value) => (
    <Badge key={value} variant="success">
      {value}
    </Badge>
  ))

const Price = ({ value }: { value: number | null }) =>
  value === null ? (
    <span className={mutedSupporting}>Цена не указана</span>
  ) : (
    <span className="whitespace-nowrap">
      <strong className={typeClass.sectionTitle}>от {numberFormat.format(value / 100)} ₽</strong>
      <span className={mutedCaption}> / шт.</span>
    </span>
  )

const marketplaceTags = (item: Fulfillment) =>
  item.marketplaces.map(({ marketplace, otherName }) => ({
    marketplace,
    label: marketplace === 'other' && otherName ? otherName : marketplaceLabels[marketplace],
  }))

const geographyName = (item: Fulfillment) => {
  const areas = Object.values(item.serviceAreas).filter((area) => area !== undefined)
  if (areas.some((area) => area.mode === 'all')) {
    return 'Вся Россия'
  }
  const cities = Array.from(new Set(areas.flatMap((area) => area.cities.map((city) => city.name))))
  return cities.length ? cities.join(', ') : 'Не указана'
}

/**
 * Контакты и ссылка на Яндекс живут внутри карточки — из списка ведём только на неё,
 * чтобы каждое обращение к фулфилменту попадало в счётчик просмотров карточки.
 * Ссылка на названии растянута на всю карточку, поэтому кнопки подняты над ней (relative).
 */
export const FulfillmentCard = ({ item }: { item: Fulfillment }) => {
  const facts = businessFacts(item)

  return (
    <Card className="relative" role="article" aria-label={item.name}>
      <CardContent className="grid gap-4 md:grid-cols-[auto_minmax(0,1fr)_230px_250px_190px] md:items-start">
        <FulfillmentLogo item={item} />

        <div className="grid min-w-0 gap-2">
          <div className="grid gap-0.5">
            <Link
              href={`/fulfillment/${item.slug}`}
              className={`${typeClass.sectionTitle} after:absolute after:inset-0`}
            >
              {item.name}
            </Link>
            {facts && <span className={mutedCaption}>{facts}</span>}
          </div>

          <div className="flex flex-wrap gap-1.5">
            <ShipmentTypeTags values={item.shipmentTypes.map((type) => shipmentTypeLabels[type])} />
          </div>

          <p className={mutedSupporting}>География: {geographyName(item)}</p>
        </div>

        <div className="grid min-w-0 content-start gap-2 md:border-l md:pl-4">
          <Ratings item={item} />
        </div>

        <div className="grid min-w-0 content-start gap-1.5 md:border-l md:pl-4">
          <span className={mutedCaption}>Площадки</span>
          <div className="flex flex-wrap gap-1.5">
            {marketplaceTags(item).map(({ marketplace, label }) => (
              <MarketplaceTag key={label} marketplace={marketplace} label={label} />
            ))}
          </div>
        </div>

        <div className="relative grid gap-2 md:justify-items-end">
          <Price value={item.minimumUnitPrice} />
          <Button
            size="sm"
            className="w-full"
            render={<Link href={`/fulfillment/${item.slug}`} aria-label={`Открыть карточку ${item.name}`} />}
          >
            <WarehouseIcon className="size-4" aria-hidden />
            Открыть карточку ФФ
          </Button>
          {item.canEdit && (
            <Button
              size="sm"
              variant="ghost"
              className="w-full"
              render={<Link href={`/fulfillment/${item.slug}/edit`} aria-label={`Редактировать ${item.name}`} />}
            >
              <SquarePenIcon className="size-4" aria-hidden />
              Редактировать
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
